"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAgent = exports.Agent = void 0;
const validator_1 = require("./validator");
const ValidationError_1 = __importDefault(require("./schemas/ValidationError"));
const debug_1 = __importDefault(require("debug"));
const events_1 = require("events");
const coreEvents_1 = require("./coreEvents");
/**
 * Filters unauthorized methods. By default all methods are authorized
 * @internal
 */
const filterUnauthorizedMethods = (methods, authorizedMethods) => {
    if (!authorizedMethods) {
        return methods;
    }
    const result = {};
    for (const methodName of Object.keys(methods)) {
        if (authorizedMethods.includes(methodName)) {
            result[methodName] = methods[methodName];
        }
    }
    return result;
};
/**
 * Provides a common context for all plugin methods.
 *
 * This is the main entry point into the API of the DID Agent Framework.
 * When plugins are installed, they extend the API of the agent and the methods
 * they provide can all use the common context so that plugins can build on top
 * of each other and create a richer experience.
 *
 * @public
 */
class Agent {
    /**
     * Constructs a new instance of the `Agent` class
     *
     * @param options - Configuration options
     * @public
     */
    constructor(options) {
        /**
         * The map of plugin + override methods
         */
        this.methods = {};
        this.protectedMethods = ['execute', 'availableMethods', 'emit'];
        this.eventBus = new events_1.EventEmitter();
        this.eventQueue = [];
        this.context = options === null || options === void 0 ? void 0 : options.context;
        this.schema = {
            components: {
                schemas: Object.assign({}, ValidationError_1.default.components.schemas),
                methods: {},
            },
        };
        if (options === null || options === void 0 ? void 0 : options.plugins) {
            for (const plugin of options.plugins) {
                this.methods = Object.assign(Object.assign({}, this.methods), filterUnauthorizedMethods(plugin.methods || {}, options.authorizedMethods));
                if (plugin.schema) {
                    this.schema = {
                        components: {
                            schemas: Object.assign(Object.assign({}, this.schema.components.schemas), plugin.schema.components.schemas),
                            methods: Object.assign(Object.assign({}, this.schema.components.methods), plugin.schema.components.methods),
                        },
                    };
                }
                if ((plugin === null || plugin === void 0 ? void 0 : plugin.eventTypes) && (plugin === null || plugin === void 0 ? void 0 : plugin.onEvent)) {
                    for (const eventType of plugin.eventTypes) {
                        this.eventBus.on(eventType, (args) => {
                            var _a;
                            const promise = (_a = plugin === null || plugin === void 0 ? void 0 : plugin.onEvent) === null || _a === void 0 ? void 0 : _a.call(plugin, { type: eventType, data: args }, Object.assign(Object.assign({}, this.context), { agent: this }));
                            this.eventQueue.push(promise);
                            promise === null || promise === void 0 ? void 0 : promise.catch((rejection) => {
                                if (eventType !== coreEvents_1.CoreEvents.error) {
                                    this.eventBus.emit(coreEvents_1.CoreEvents.error, rejection);
                                }
                                else {
                                    this.eventQueue.push(Promise.reject(new Error('ErrorEventHandlerError: throwing an error in an error handler should crash')));
                                }
                            });
                        });
                    }
                }
            }
        }
        if (options === null || options === void 0 ? void 0 : options.overrides) {
            this.methods = Object.assign(Object.assign({}, this.methods), filterUnauthorizedMethods(options.overrides, options.authorizedMethods));
        }
        for (const method of Object.keys(this.methods)) {
            if (!this.protectedMethods.includes(method)) {
                //@ts-ignore
                this[method] = (args) => __awaiter(this, void 0, void 0, function* () { return this.execute(method, args); });
            }
        }
        this.schemaValidation = (options === null || options === void 0 ? void 0 : options.schemaValidation) || false;
    }
    /**
     * Lists available agent method names
     *
     * @returns a list of available methods
     * @public
     */
    availableMethods() {
        return Object.keys(this.methods);
    }
    /**
     * Returns agent plugin schema
     *
     * @returns agent plugin schema
     * @public
     */
    getSchema() {
        return this.schema;
    }
    /**
     * Executes a plugin method.
     *
     * Normally, the `execute()` method need not be called.
     * The agent will expose the plugin methods directly on the agent instance
     * but this can be used when dynamically deciding which methods to call.
     *
     * @remarks
     * Plugin method will receive a context object as a second argument.
     * Context object always has `agent` property that is the `Agent` instance that is executing said method
     *
     * @param method - method name
     * @param args - arguments object
     * @example
     * ```typescript
     * await agent.execute('foo', { bar: 'baz' })
     *
     * // is equivalent to:
     * await agent.foo({ bar: 'baz' })
     * ```
     * @public
     */
    execute(method, args) {
        return __awaiter(this, void 0, void 0, function* () {
            (0, debug_1.default)('veramo:agent:' + method)('%o', args);
            if (!this.methods[method])
                throw Error('Method not available: ' + method);
            const _args = args || {};
            if (this.schemaValidation && this.schema.components.methods[method]) {
                (0, validator_1.validateArguments)(method, _args, this.schema);
            }
            const result = yield this.methods[method](_args, Object.assign(Object.assign({}, this.context), { agent: this }));
            if (this.schemaValidation && this.schema.components.methods[method]) {
                (0, validator_1.validateReturnType)(method, result, this.schema);
            }
            (0, debug_1.default)('veramo:agent:' + method + ':result')('%o', result);
            return result;
        });
    }
    /**
     * Broadcasts an `Event` to potential listeners.
     *
     * Listeners are `IEventListener` instances that declare `eventTypes`
     * and implement an `async onEvent({type, data}, context)` method.
     * Note that `IAgentPlugin` is also an `IEventListener` so plugins can be listeners for events.
     *
     * During creation, the agent automatically registers listener plugins
     * to the `eventTypes` that they declare.
     *
     * Events are processed asynchronously, so the general pattern to be used is fire-and-forget.
     * Ex: `agent.emit('foo', {eventData})`
     *
     * In situations where you need to make sure that all events in the queue have been exhausted,
     * the `Promise` returned by `emit` can be awaited.
     * Ex: `await agent.emit('foo', {eventData})`
     *
     * In case an error is thrown while processing an event, the error is re-emitted as an event
     * of type `CoreEvents.error` with a `EventListenerError` as payload.
     *
     * Note that `await agent.emit()` will NOT throw an error. To process errors, use a listener
     * with `eventTypes: [ CoreEvents.error ]` in the definition.
     *
     * @param eventType - the type of event being emitted
     * @param data - event payload.
     *     Use the same `data` type for events of a particular `eventType`.
     *
     * @public
     */
    emit(eventType, data) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            this.eventBus.emit(eventType, data);
            while (this.eventQueue.length > 0) {
                try {
                    yield this.eventQueue.shift();
                }
                catch (e) {
                    //nop
                    if (typeof (e === null || e === void 0 ? void 0 : e.message) === 'string' && ((_a = e === null || e === void 0 ? void 0 : e.message) === null || _a === void 0 ? void 0 : _a.startsWith('ErrorEventHandlerError'))) {
                        throw e;
                    }
                }
            }
        });
    }
}
exports.Agent = Agent;
/**
 * Helper function to create a new instance of the {@link Agent} class with correct type
 *
 * @remarks
 * Use {@link TAgent} to configure agent type (list of available methods) for autocomplete in IDE
 *
 * @example
 * ```typescript
 * import { createAgent, IResolver, IMessageHandler } from '@veramo/core'
 * import { AgentRestClient } from '@veramo/remote-client'
 * import { CredentialIssuer, ICredentialIssuer } from '@veramo/credential-w3c'
 * const agent = createAgent<IResolver & IMessageHandler & ICredentialIssuer>({
 *   plugins: [
 *     new CredentialIssuer(),
 *     new AgentRestClient({
 *       url: 'http://localhost:3002/agent',
 *       enabledMethods: [
 *         'resolveDid',
 *         'handleMessage',
 *       ],
 *     }),
 *   ],
 * })
 * ```
 * @param options - Agent configuration options
 * @returns configured agent
 * @public
 */
function createAgent(options) {
    //@ts-ignore
    return new Agent(options);
}
exports.createAgent = createAgent;
//# sourceMappingURL=agent.js.map